// src/pages/onboarding/employer/EmployerOnboarding.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingTopBar   from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection  from "@/components/onboarding/OnboardingSection";
import Button             from "@/components/ui/Button";
import UploadDropzone     from "@/components/upload/UploadDropzone.jsx";
import TextField56        from "@/sot/primitives/TextField56.jsx";
import BasicDialog        from "@/components/ui/BasicDialog.jsx";
import Snackbar           from "@/sot/primitives/Snackbar.jsx";
import { logout }         from "@/lib/auth/logout.js";
import StepOtp            from "@/pages/onboarding/shared/steps/StepOtp.jsx";
import { sendEmailVerification } from "@/lib/notify/sendEmailVerification.js";
import SelectCountrySADC from "@/components/fields/SelectCountrySADC.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";

// NEW unified payments callers + Stripe
import { emailInvoice, createPaymentIntent } from "@/lib/payments/api";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

const LS_KEY = "onb_employer_v1";
const STRIPE_PK = import.meta.env.VITE_STRIPE_PK_TEST || "";
// Legacy employer collection is held. Do not initialize a retired provider at app startup.
const stripePromise = Promise.resolve(null);

async function sendInvoiceEmailStub({ to, summary }) {
  // TODO: wire to your edge function / email provider
  console.log("Emailing invoice to:", to, summary);
  return { ok: true };
}

async function handleFiles(fs = []) {
  setFiles(fs);

  // reset if nothing
  if (!fs || fs.length === 0) {
    setHeadcount(0);
    return;
  }

  const file = fs[0];

  try {
    const text = await file.text();
    const lines = text.trim().split(/\r?\n/);

    if (lines.length <= 1) {
      setHeadcount(0);
      return;
    }

    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const dependIndex = header.findIndex((h) => h.includes("depend"));

    let adults = 0;
    let dependants = 0;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const cols = line.split(",");
      adults += 1;

      if (dependIndex !== -1 && cols[dependIndex]) {
        const n = parseInt(cols[dependIndex].trim(), 10);
        if (!Number.isNaN(n) && n > 0) {
          dependants += n;
        }
      }
    }

    setHeadcount(adults + dependants);
  } catch (err) {
    console.error("Error parsing CSV", err);
    setHeadcount(0);
  }
}


/* ---------- Currency helpers (public USD) ---------- */
function usd(n){ return new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(n); }
const DIASPORA_CCS = new Set(["+61","+1","+49","+33","+64","+41","+971","+44"]); // AU, US/CA, DE, FR, NZ, CH, AE, UK
const DIASPORA_MULTIPLIER = 1.5; // tweak easily

/* ---------- Step 1: Company ---------- */
function StepCompany({ step, total, values, onChange, onBack, onContinue }) {
  const canContinue = values.legalName?.trim() && values.registration?.trim() && values.country?.trim();
  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Company details</h2>}
        footer={<Button fullWidth disabled={!canContinue} onClick={onContinue}>Continue</Button>}
      >
        <div className="grid gap-3">
          <TextField56 label="Legal name*" value={values.legalName} onChange={(v) => onChange({ legalName: v })} />
          <TextField56 label="Registration number*" value={values.registration} onChange={(v) => onChange({ registration: v })} />
          <TextField56 label="Address line 1" value={values.addr1} onChange={(v) => onChange({ addr1: v })} />
          <TextField56 label="Address line 2" value={values.addr2} onChange={(v) => onChange({ addr2: v })} />
          <div className="grid grid-cols-2 gap-3">
            <TextField56 label="City" value={values.city} onChange={(v) => onChange({ city: v })} />
            <TextField56 label="Region / State" value={values.region} onChange={(v) => onChange({ region: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
  <TextField56
    label="Postal code"
    value={values.postal}
    onChange={(v) => onChange({ postal: v })}
  />
  {/* Country gate (SADC + Diaspora only) */}
  <SelectCountrySADC
   label="Country*"
   value={values.country}
   onChange={(v) => onChange({ country: v })}
 />
</div>
        </div>
      </OnboardingSection>
    </div>
  );
}

/* ---------- Step 2: Primary contact (uses your InputPhoneSADC) ---------- */
function StepContact({ step, total, values, onChange, onBack, onContinue }) {
  const hasPhone = values.phone?.trim()?.length >= 6 && !!values.phoneCc;
  const canContinue =
    values.name?.trim() &&
    values.email?.trim() &&
    hasPhone &&
    values.position?.trim() &&
    values.department?.trim();

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Primary contact</h2>}
        footer={<Button fullWidth disabled={!canContinue} onClick={onContinue}>Continue</Button>}
      >
        <div className="grid gap-3">
          <TextField56 label="Full name*" value={values.name} onChange={(v)=>onChange({ name: v })} />
          <TextField56 label="Email*"     value={values.email} onChange={(v)=>onChange({ email: v })} />

          <InputPhoneSADC
            label="Phone*"
            value={values.phone}          // local digits only
            cc={values.phoneCc}           // like "+267"
            onChange={(digits)=>onChange({ phone: digits })}
            onChangeCc={(cc)=>onChange({ phoneCc: cc })}
          />

          <div className="grid grid-cols-2 gap-3">
            <TextField56 label="Position*"   value={values.position}   onChange={(v)=>onChange({ position: v })} />
            <TextField56 label="Department*" value={values.department} onChange={(v)=>onChange({ department: v })} />
          </div>
        </div>
      </OnboardingSection>
    </div>
  );
}

/* ---------- Step 3: Verify contact ---------- */
function StepVerifyContact({ step, total, email, phoneDisplay, status, setStatus, onBack, onContinue, onGotoPhoneOtp }) {
  const sending = status.sending === true;
  const emailDone = !!status.emailVerified;
  const phoneDone = !!status.phoneVerified;
  const canContinue = emailDone && phoneDone;

  const sendLink = async () => {
    setStatus((s) => ({ ...s, sending: true }));
    const res = await sendEmailVerification(email).catch(() => ({ ok: false }));
    setStatus((s) => ({ ...s, sending: false, emailVerified: !!res?.ok }));
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Verify contact</h2>}
        footer={<Button fullWidth disabled={!canContinue} onClick={onContinue}>Continue</Button>}
      >
        <div className="grid gap-4">
          <div className="rounded-2xl bg-white p-4">
            <div className="text-title-medium mb-1">Verify email (admin)</div>
            <p className="text-body-medium text-on-surface-var">We’ll send a confirmation link to <span className="font-medium">{email || "—"}</span>.</p>
            <div className="mt-3 flex gap-3">
              <Button
                variant="secondary"
                className="bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(77,163,255,0.18)]"
                disabled={!email || sending || emailDone}
                onClick={sendLink}
              >
                {emailDone ? "Email verified ✓" : sending ? "Sending…" : "Send verification link"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-4">
            <div className="text-title-medium mb-1">Verify phone (OTP)</div>
            <p className="text-body-medium text-on-surface-var">We’ll send a one-time code to <span className="font-medium">{phoneDisplay || "—"}</span>.</p>
            <div className="mt-3 flex gap-3">
              <Button
                variant="secondary"
                className="bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(77,163,255,0.18)]"
                disabled={!phoneDisplay || phoneDone}
                onClick={onGotoPhoneOtp}
              >
                {phoneDone ? "Phone verified ✓" : "Verify phone"}
              </Button>
            </div>
          </div>
        </div>
      </OnboardingSection>
    </div>
  );
}

/* ---------- Step 4: Plan (USD, auto-uplift for Diaspora) ---------- */
function StepPlan({ step, total, values, onChange, onBack, onContinue, contactCc }) {
  const plans = [
    { id: "basic",    name: "Basic",    note: "Essential coverage", rate: 7 },   // USD per employee / month
    { id: "standard", name: "Standard", note: "Most popular",       rate: 9 },
    { id: "premium",  name: "Premium",  note: "Enhanced coverage",  rate: 12 },
  ];
  const uplift = DIASPORA_CCS.has(contactCc) ? DIASPORA_MULTIPLIER : 1;
  const canContinue = !!values.plan;

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Choose your plan</h2>}
        footer={<Button fullWidth disabled={!canContinue} onClick={onContinue}>Continue</Button>}
      >
        <div className="grid gap-2">
          {plans.map((p) => {
            const shown = Math.round(p.rate * uplift);
            return (
              <label key={p.id} className="flex items-center gap-3 rounded-2xl border p-3 bg-white">
                <input
                  type="radio"
                  name="plan"
                  checked={values.plan === p.id}
                  onChange={() => onChange({ plan: p.id, planRate: shown })}
                />
                <div className="flex-1">
                  <div className="text-body-large">{p.name}</div>
                  <div className="text-body-small text-on-surface-var">
                    {p.note} — {usd(shown)}/employee/month{uplift > 1 ? " (Diaspora rate)" : ""}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </OnboardingSection>
    </div>
  );
}

/* ---------- Step 5: Upload CSV ---------- */
function StepEmployerCsv({ step, total, onBack, onContinue, files, setFiles, setHeadcount }) {
  const [localCount, setLocalCount] = useState(0);

  async function handleFiles(fs = []) {
    setFiles(fs);
    if (!fs?.length) { setLocalCount(0); setHeadcount?.(0); return; }

    // read first file, count non-empty data rows (header aware, tolerant)
    try {
      const text = await fs[0].text();
      const lines = text.split(/\r?\n/).map(l => l.trim());
      const nonEmpty = lines.filter(Boolean);
      const hasHeader = /name|first|last|email|phone|adult|depend/i.test(nonEmpty[0] || "");
      const rows = hasHeader ? nonEmpty.slice(1) : nonEmpty;
      const count = rows.filter(Boolean).length;
      setLocalCount(count);
      setHeadcount?.(count);
    } catch {
      setLocalCount(0);
      setHeadcount?.(0);
    }
  }

  const canContinue = localCount > 0;

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Upload employees</h2>}
        footer={<Button fullWidth disabled={!canContinue} onClick={onContinue}>Continue</Button>}
      >
        <div className="space-y-3">
          <div className="w-full flex items-center justify-center gap-2">
            <img src="/icons/download.svg" alt="" className="h-5 w-5 opacity-80" />
            <a href="/data/afrimed-employees-template.csv" download className="text-[rgb(var(--afrimed-primary))] no-underline">
              Download CSV template
            </a>
          </div>

          <UploadDropzone accept=".csv" maxSizeMB={10} onFiles={handleFiles} />

          <div className="text-body-medium text-on-surface-var text-center">
            {localCount ? `Detected ${localCount} people` : "Add your CSV to continue"}
          </div>
        </div>
      </OnboardingSection>
    </div>
  );
}

/* ---------- Step 6: Invoice preview (USD) ---------- */
function InvoiceModal({ open, onClose, invoice }) {
  if (!open) return null;
  const { employees, planRate, subtotal, vat, totalDue, accountsEmail } = invoice;
  const [invoiceNo, setInvoiceNo] = useState(() => `#${Date.now().toString().slice(-8)}`);

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/35" onClick={onClose} />
      <div className="relative w-[min(920px,92vw)] rounded-[20px] overflow-hidden bg-white shadow-[0_20px_60px_-20px_rgba(24,50,119,.20)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{borderColor:"rgba(24,50,119,0.12)"}}>
          <div className="text-title-medium">AfriMed™ Proforma Invoice</div>
          <div className="text-label-medium text-on-surface-var">{invoice?.invoiceNo || ""}</div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 grid gap-6">
          {/* Brand block */}
          <div className="flex items-start justify-between">
            <div>
              <div className="text-title-large">AfriMed™ Telehealth (Pty) Ltd</div>
              <div className="text-body-medium text-on-surface-var">
                19 Rontree Avenue, Bakoven, Western Cape 8007 • billing@afrimed.tech
              </div>
            </div>
            <img src="https://edfwmllxohmptmojedyo.supabase.co/storage/v1/object/public/brand/AfriMed-Google-logo-320%20x%20132.png" alt="" className="h-10 opacity-90" />
          </div>

          {/* Bill-to + summary */}
<div className="grid sm:grid-cols-2 gap-4">
  {/* Bill to */}
  <div
    className="rounded-xl border overflow-hidden bg-white"
    style={{ borderColor: "rgba(24,50,119,0.12)" }}
  >
    <div className="px-4 py-2 bg-[rgba(77,163,255,0.12)] text-label-medium text-on-surface-var">
      Bill to
    </div>
    <div className="px-4 pb-4 pt-3">
      <div className="text-body-large">
        {invoice.billTo?.name || "Your Company"}
      </div>
      <div className="text-body-medium text-on-surface-var">
        {invoice.billTo?.email || "accounts@company.com"}
      </div>

      {(invoice.billTo?.address1 || invoice.billTo?.city) && (
        <div className="text-body-medium text-on-surface-var mt-1">
          {[invoice.billTo.address1, invoice.billTo.address2]
            .filter(Boolean)
            .join(", ")}
          <br />
          {[invoice.billTo.city, invoice.billTo.region, invoice.billTo.postal]
            .filter(Boolean)
            .join(", ")}
          <br />
          {invoice.billTo.country}
        </div>
      )}
    </div>
  </div>

  {/* Summary */}
  <div
    className="rounded-xl border overflow-hidden bg-white"
    style={{ borderColor: "rgba(24,50,119,0.12)" }}
  >
    <div className="px-4 py-2 bg-[rgba(77,163,255,0.12)] text-label-medium text-on-surface-var">
      Summary
    </div>
    <div className="px-4 pb-4 pt-3">
      <div className="flex justify-between mb-1">
        <span>Employees</span>
        <span>{invoice.employees}</span>
      </div>
      <div className="flex justify-between">
        <span>Rate</span>
        <span>{usd(invoice.planRate)}/employee/month</span>
      </div>
    </div>
  </div>
</div>

          {/* Line items */}
          <div className="rounded-xl overflow-hidden border" style={{borderColor:"rgba(24,50,119,0.12)"}}>
  <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 px-5 py-3 text-label-medium bg-[rgba(77,163,255,0.12)]">
    <div>Description</div><div className="text-right">Qty</div><div className="text-right">Amount</div>
  </div>
  <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 px-5 py-3">
    <div>AfriMed Workforce Plan — monthly subscription</div>
    <div className="text-right">{employees}</div>
    <div className="text-right">{usd(subtotal)}</div>
  </div>
  <div className="h-px bg-[rgba(24,50,119,0.08)]" />
  <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 px-5 py-3">
    <div className="text-on-surface-var">VAT (12%)</div><div />
    <div className="text-right">{usd(vat)}</div>
  </div>
  <div className="h-px bg-[rgba(24,50,119,0.08)]" />
  <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 px-5 py-4">
    <div className="text-title-medium">Total Due</div><div />
    <div className="text-right text-title-medium">{usd(totalDue)}</div>
  </div>
</div>
        </div>

        {/* Footer */}
<div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t" style={{borderColor:"rgba(24,50,119,0.12)"}}>
  <button
    className="min-h-[44px] px-4 py-2 rounded-lg text-label-large bg-[rgba(24,50,119,0.08)]"
    onClick={onClose}
  >
    Close
  </button>

  <button
    className="min-h-[44px] px-4 py-2 rounded-lg text-label-large bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))]"
    onClick={()=>{
      const blob = new Blob([JSON.stringify(invoice, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "afrimed-invoice.json";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(()=>URL.revokeObjectURL(url), 0);
    }}
  >
    Download
  </button>

  <button
    className="min-h-[44px] px-4 py-2 rounded-lg text-label-large bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))]"
    onClick={async ()=>{
      await sendInvoiceEmailStub({ to: accountsEmail, summary: invoice });
      document.dispatchEvent(new CustomEvent("afrimed_toast", { detail: "Invoice emailed." }));
    }}
  >
    Email
  </button>
</div>
      </div>
    </div>
  );
}

function StepInvoice({
  step, total,
  planRate, employees,
  accountsEmail, setAccountsEmail,
  company, contact, invoiceNo,
  onBack, onContinue
}) {
  const subtotal = employees * (planRate || 0);
  const vat      = Math.round(subtotal * 0.12);
  const totalDue = subtotal + vat;

  const [openPreview, setOpenPreview] = useState(false);

  const billTo = {
    name:    company?.legalName || "Your Company",
    email:   contact?.email     || "accounts@company.com",
    address1: company?.addr1 || "",
    address2: company?.addr2 || "",
    city:     company?.city  || "",
    region:   company?.region|| "",
    postal:   company?.postal|| "",
    country:  company?.country|| "",
  };

  const invoice = {
    invoiceNo,
    companyName: billTo.name,
    employees, planRate, subtotal, vat, totalDue,
    accountsEmail,
    billTo
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Invoice preview</h2>}
        footer={<Button fullWidth onClick={onContinue}>Proceed to payment</Button>}
      >
        <div className="rounded-2xl bg-white p-4 grid gap-3">
          <div className="flex justify-between text-body-large"><span>Employees</span><span>{employees}</span></div>
          <div className="flex justify-between text-body-large"><span>Rate</span><span>{usd(planRate)}/employee/month</span></div>
          <div className="h-px bg-[rgba(0,0,0,.08)]" />
          <div className="flex justify-between"><span>Subtotal</span><span>{usd(subtotal)}</span></div>
          <div className="flex justify-between"><span>VAT (12%)</span><span>{usd(vat)}</span></div>
          <div className="flex justify-between text-title-medium"><span>Total</span><span>{usd(totalDue)}</span></div>

          <div className="h-px bg-[rgba(0,0,0,.08)] my-1" />
          <TextField56
            label="Accounts email (invoice recipient)"
            value={accountsEmail}
            onChange={(v) => setAccountsEmail(v)}
            placeholder="accounts@yourcompany.com"
          />

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] no-underline"
              onClick={() => setOpenPreview(true)}
            >
              View invoice
            </Button>

            <Button
              variant="secondary"
              className="bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] no-underline"
              onClick={async ()=>{
                const res = await emailInvoice({
                  to: accountsEmail,
                  invoice: {
                    invoiceNo,
                    companyName: billTo.name,
                    employees,
                    planRate: Math.round((planRate || 0) * 100),
                    subtotal: Math.round(subtotal * 100),
                    vat: Math.round(vat * 100),
                    totalDue: Math.round(totalDue * 100),
                    accountsEmail,
                    billTo,
                    logoUrl: `${window.location.origin}/brand/AfriMed-Google-logo-320%20x%20132.png`
                  }
                });
                document.dispatchEvent(new CustomEvent("afrimed_toast", { detail: res?.ok ? "Invoice emailed." : "Email failed" }));
              }}
            >
              Email invoice
            </Button>
          </div>
        </div>
      </OnboardingSection>

      <InvoiceModal
        open={openPreview}
        onClose={()=>setOpenPreview(false)}
        invoice={invoice}
      />
    </div>
  );
}

/* ---------- Step 7: Payment (Stripe USD) ---------- */
function CardPay({ amountCents, onPaid, onPending }) {
  const stripe = useStripe();
  const elements = useElements();
  const pay = async () => {
    if (!stripe || !elements) return;
    onPending?.();
    const { clientSecret, error } = await createPaymentIntent({ currency: "usd", amount: amountCents, metadata: { src: "employer_onboarding" }});
    if (error) { onPending?.("error"); alert(error); return; }
    const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
    if (result.error) { onPending?.("error"); alert(result.error.message); return; }
    onPaid?.(result.paymentIntent?.id);
  };
  return (
    <div className="grid gap-3">
      <div className="rounded-xl border bg-white p-3"><CardElement options={{ hidePostalCode: true }} /></div>
      <Button onClick={pay}>Pay</Button>
    </div>
  );
}
/* After StepInvoice → proceed to payment */
function StepPayment({ step, total, amountCents, invoiceNo, onBack, onFinish }) {
  const [method, setMethod] = useState("eft"); // "eft" | "card"

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={<h2 className="text-title-large text-on-surface">Payment</h2>}
        footer={<Button fullWidth onClick={onFinish}>Finish</Button>}
      >
        <div className="inline-flex rounded-xl border overflow-hidden mb-4">
          <button
            className={`px-4 py-2 text-label-large ${method==="eft"?"bg-[rgba(77,163,255,0.12)]":""}`}
            onClick={()=>setMethod("eft")}
          >Bank transfer</button>
          <button
            className={`px-4 py-2 text-label-large ${method==="card"?"bg-[rgba(77,163,255,0.12)]":""}`}
            onClick={()=>setMethod("card")}
          >Card</button>
        </div>

        {method==="eft" ? (
          <div className="rounded-xl bg-white p-4">
            <div>Bank: UBS, Aarau</div>
            <div>Beneficiary: Ndabezinhle Ndlovu</div>
            <div>IBAN: CH40 0023 1231 3585 93M1 E</div>
            <div>BIC: UBSWCHZH80A</div>
            <div>Reference: <span className="font-medium">{invoiceNo}</span></div>
          </div>
        ) : (
          // Your CardCheckout/PaymentElement flow
          <CardCheckout amount_cents={amountCents} customer_email={/* contact.email */ undefined}
            onSuccess={()=>document.dispatchEvent(new CustomEvent("afrimed_toast",{detail:"Payment successful"}))}
            onFail={(m)=>document.dispatchEvent(new CustomEvent("afrimed_toast",{detail:m||"Payment failed"}))}
          />
        )}
      </OnboardingSection>
    </div>
  );
}

function CardCheckoutInner({ amount_cents, customer_email, onSuccess, onFail }) {
  const stripe = useStripe();
  const elements = useElements();

  async function payNow() {
    if (!stripe || !elements) return;
    const { error } = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.origin + "/home" }, redirect: "if_required" });
    if (error) onFail?.(error.message);
    else onSuccess?.();
  }

  return (
    <div className="grid gap-3">
      <PaymentElement />
      <Button onClick={payNow}>Pay now</Button>
    </div>
  );
}

function CardCheckout({ amount_cents, customer_email, onSuccess, onFail }) {
  const [clientSecret, setClientSecret] = useState("");
  useEffect(() => {
    (async () => {
      const r = await createPaymentIntent({ amount_cents, currency: "usd", customer_email });
      setClientSecret(r?.clientSecret || "");
    })();
  }, [amount_cents, customer_email]);

  if (!clientSecret) return <div className="text-body-medium text-on-surface-var">Preparing payment…</div>;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardCheckoutInner amount_cents={amount_cents} customer_email={customer_email} onSuccess={onSuccess} onFail={onFail} />
    </Elements>
  );
}

/* ---------- Router ---------- */
export default function EmployerOnboarding({ backToCategory }) {
  const navigate = useNavigate();
  // Steps: 0 Company, 1 Contact, 2 Verify, 3 Plan, 4 CSV, 5 Invoice, 6 Payment, 7 Finish (dialog)
  const [step, setStep] = useState(0);
  const TOTAL = 7;

  const [headcount, setHeadcount] = useState(0);

  const [company, setCompany] = useState({ legalName:"", registration:"", addr1:"", addr2:"", city:"", region:"", postal:"", country:"" });
  const [contact, setContact] = useState({
    name:"", email:"", phone:"", phoneCc:"+267", position:"", department:""
  }); // 🔑 phone = digits only, phoneCc = +code
  const [verify, setVerify] = useState({ emailVerified:false, phoneVerified:false, sending:false });
  const [plan, setPlan] = useState({ plan:"", planRate:0 });
  const [files, setFiles] = useState([]);
  const [accountsEmail, setAccountsEmail] = useState("");

  const [showFinish, setShowFinish] = useState(false);
  const [toast, setToast] = useState("");

  const invoiceNoRef = useRef(null);
if (!invoiceNoRef.current) {
  const y = new Date().getFullYear();
  const rand = Math.floor(Math.random() * 1e8).toString().padStart(8, "0");
  invoiceNoRef.current = `INV-${y}-${rand}`;
}
const invoiceNo = invoiceNoRef.current;

  useEffect(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    try {
      const v = JSON.parse(raw);
      if (typeof v.step === "number") setStep(v.step);
      if (v.company) setCompany(v.company);
      if (v.contact) setContact(v.contact);
      if (v.verify) setVerify(v.verify);
      if (v.plan) setPlan(v.plan);
      if (v.files) setFiles(v.files);
      if (v.accountsEmail) setAccountsEmail(v.accountsEmail);
    } catch {}
  }, []);
  useEffect(() => {
    const payload = { step, company, contact, verify, plan, files, accountsEmail };
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch {}
  }, [step, company, contact, verify, plan, files, accountsEmail]);

  const phoneFull = `${contact.phoneCc || ""}${contact.phone || ""}`;

  const handleFinishOk = async () => { setShowFinish(false); setToast("Profile creation starting…"); setTimeout(async () => { await logout(); navigate("/login?sheet=1"); }, 900); };
  const handleFinishCancel = async () => { setShowFinish(false); setToast("Logged out"); setTimeout(async () => { await logout(); navigate("/login"); }, 900); };

  useEffect(() => {
  const handler = (e) => setToast(String(e.detail || ""));
  document.addEventListener("afrimed_toast", handler);
  return () => document.removeEventListener("afrimed_toast", handler);
}, []);

  if (step === 2.5) {
    return (
      <StepOtp
        step={3} total={TOTAL}
        phone={phoneFull}
        onBack={() => setStep(2)}
        onContinue={() => { setVerify((v) => ({ ...v, phoneVerified: true })); setStep(2); }}
      />
    );
  }

  if (step === 0) return (
    <StepCompany 
    step={1} total={TOTAL} 
    values={company} 
    onChange={(p) => setCompany((s) => ({ ...s, ...p }))} 
    onBack={backToCategory} onContinue={() => setStep(1)} />
  );

  if (step === 1) return (
    <StepContact step={2} total={TOTAL} 
    values={contact} onChange={(p) => setContact((s) => ({ ...s, ...p }))} 
    onBack={() => setStep(0)} onContinue={() => setStep(2)} />
  );

  if (step === 2) return (
    <StepVerifyContact
      step={3} total={TOTAL}
      email={contact.email}
      phoneDisplay={phoneFull}
      status={verify}
      setStatus={setVerify}
      onBack={() => setStep(1)}
      onGotoPhoneOtp={() => setStep(2.5)}
      onContinue={() => setStep(3)}
    />
  );

  if (step === 3) return (
    <StepPlan
      step={4} total={TOTAL}
      values={plan}
      onChange={(p) => setPlan((s) => ({ ...s, ...p }))}
      onBack={() => setStep(2)}
      onContinue={() => setStep(4)}
      contactCc={contact.phoneCc}
    />
  );

  if (step === 4) return (
  <StepEmployerCsv
    step={5} total={TOTAL}
    onBack={() => setStep(3)}
    onContinue={() => setStep(5)}
    files={files}
    setFiles={setFiles}
    setHeadcount={setHeadcount}   // <-- add this
  />
);

  if (step === 5) {
  const employees = Math.max(1, headcount || 1);  // ✅ guard
  return (
    <StepInvoice
      step={6} total={TOTAL}
      planRate={plan.planRate}
      employees={employees}
      accountsEmail={accountsEmail}
      setAccountsEmail={setAccountsEmail}
      company={company}              // make sure these three are present
      contact={contact}
      invoiceNo={invoiceNo}
      onBack={() => setStep(4)}
      onContinue={() => setStep(6)}
    />
  );
}

  if (step === 6) {
  const employees = Math.max(1, headcount || 1);  // ✅ guard
  const amountCents = Math.round((plan.planRate || 0) * employees * 100);
  return (
    <>
      <StepPayment
        step={7} total={TOTAL}
        amountCents={amountCents}
        invoiceNo={invoiceNo}        // shows in EFT reference
        onBack={() => setStep(5)}
        onFinish={() => setShowFinish(true)}/>

        <Snackbar open={!!toast} autoHideMs={null} onClose={() => setToast("")}> {toast} </Snackbar>

        <BasicDialog
  open={showFinish}
  title="Create a personal profile?"
  body="Would you like to create a profile as a patient or as a doctor? If yes, follow the onscreen steps."
  cancelText="No, Log Out"
  okText="Yes, Create Profile"
  onCancel={handleFinishCancel}
  onOk={handleFinishOk}
  disableBackdropClose={true}
/>
      </>
    );
  }

  return null;
}
